/**
 * The 24 back-to-birth rules of an LLP (DR-4974 §6), then the five rules of a tracked part set
 * (DR-5467): T1 missing installation record, T2 missing removal record, T3 no release certificate
 * after a shop visit, T4 P/N mismatch between documents, T5 blade S/N change without a replacement
 * record. An LLP row carries L checks only; a set row carries T checks only.
 */
export const LLP_BTB_RULE_IDS = [
    'L1',
    'L2',
    'L3',
    'L4',
    'L5',
    'L6',
    'L7',
    'L8',
    'L9',
    'L10',
    'L11',
    'L12',
    'L13',
    'L14',
    'L15',
    'L16',
    'L17',
    'L18',
    'L19',
    'L20',
    'L21',
    'L22',
    'L23',
    'L24',
    'T1',
    'T2',
    'T3',
    'T4',
    'T5',
];
/** Why a stretch of a part's life is a gap (L16, from S4). */
export const LLP_BTB_GAP_REASONS = [
    /** L16: the part's own counters are missing at one end of a stretch. */
    'missing-counters',
    /** L16: a later reading of the part's own TSN or CSN is below an earlier one. */
    'counter-regression',
    /** L16: over one installed stretch, the LLP cycles do not equal the engine cycles. */
    'cycle-delta-mismatch',
    /** L16: on one engine, the engine counters a later-dated reading prints are below an earlier one's, so the dates are out of order. */
    'date-order',
    /** Part counters advanced while no record puts the part on any engine (an off-wing span with cycles accrued). */
    'uncovered-period',
    /** A fitment period on an engine whose installation or removal record is missing (emitted from S6 with job cards; S4 does not emit it). */
    'missing-period-evidence',
];
/** What a finding is about: a rule id, or one of the five non-rule kinds (DR-4974 §7). */
export const LLP_BTB_FINDING_TYPES = [
    ...LLP_BTB_RULE_IDS,
    'source-conflict',
    'serial-near-miss',
    /**
     * A card row whose date is a typo for a later engine removal (DR-5448), or a DIS report date
     * before the engine manufacture day (DR-5502); the row stays as printed.
     */
    'document-discrepancy',
    'donor-project-missing',
    'donor-document-missing',
    /** A tracked part set (DR-5467) whose host disk the trace cannot find among the engine's LLPs. */
    'host-disk-missing',
];
/** Kind of a labelled reference printed in an ARC's Block-12 remarks (DR-4726). */
export const ARC_REFERENCE_KINDS = [
    'workscope',
    'repair-order',
    'customer-order',
    'purchase-order',
    'shop-order',
    'work-order',
];
/** Kind of a numbered compliance statement printed in an ARC's Block-12 remarks (DR-5549). */
export const ARC_COMPLIANCE_KINDS = ['ad', 'sb'];
/** Compliance status the Block-12 wording states for one AD or SB (DR-5549; vocabulary per Rinoj, 2026-10-21). */
export const ARC_COMPLIANCE_STATUSES = [
    // complied with, C/W, CW (only right next to the number), accomplished, incorporated, embodied,
    // performed, modified IAW. NOT "applied", NOT "verified" (both read `unknown`).
    'complied',
    // PCW, previously complied with, prior to induction. Kept separate from `complied`.
    'previously-complied',
    // N/A, NA, not applicable, not affected, not concerned, does not apply, no effect.
    'not-applicable',
    // NCW, not complied, open, not incorporated, not embodied.
    'not-complied',
    // A number with no compliance word in its statement, or a conflicting / qualified / ambiguous statement.
    'unknown',
];
/**
 * Canonical runtime enumeration of {@link ProjectEventFamily}, in emission order (DR-2889).
 * Keyed BY the union so the compiler fails on either side of a future change: a key that no
 * longer belongs to the union, and a family added to the union but not listed here. Every
 * consumer that needs the family list at runtime (the emitter's `FAMILY_ORDER`, the app's family
 * labels) should derive from this instead of hand-rolling its own — a bare array literal, unlike
 * a keyed `Record`, does NOT fail the compiler when the union grows, so it can silently drift.
 *
 * `satisfies` (not just the `Readonly<Record<...>>` annotation on the const) is load-bearing:
 * `Object.freeze` is generic, so a literal passed straight into it is inferred fresh FROM the
 * literal rather than checked against the annotation, and the freeze result is then only
 * assignability-checked against `PROJECT_EVENT_FAMILY_KEYS`'s type — which is structural and
 * permits excess properties. Without `satisfies` on the literal itself, a stale key left behind
 * after a family is renamed or removed would keep compiling.
 */
export const PROJECT_EVENT_FAMILY_KEYS = Object.freeze({
    engine: true,
    llp: true,
    ad: true,
    sb: true,
    ldnd: true,
    'bill-of-sale': true,
    qec: true,
    utilisation: true,
    'pma-der': true,
    workscope: true,
    nis: true,
    'thrust-conversion': true,
    'engine-movement': true,
    arc: true,
    'tsn-csn-statement': true,
    'llp-btb': true,
    'build-record': true,
    'module-exchange': true,
    'compliance-job-card-facts': true,
    'tracked-part-set': true,
    'engine-configuration-rows': true,
    'removal-tags': true,
});
export const PROJECT_EVENT_FAMILIES = Object.freeze(Object.keys(PROJECT_EVENT_FAMILY_KEYS));
/**
 * Canonical runtime enumeration of {@link ProjectUndeliveredReason} (DR-2889). Keyed BY the
 * union for the same reason as {@link PROJECT_EVENT_FAMILY_KEYS}: a bare array literal does not
 * fail the compiler when the union grows, so a copy built that way can silently stay stale while
 * every keyed-`Record` copy is forced to keep up. Consumers that validate or enumerate reasons
 * at runtime should derive from this rather than re-listing the four values themselves.
 *
 * `satisfies` on the literal is load-bearing here too — see the comment on
 * {@link PROJECT_EVENT_FAMILY_KEYS} for why the `Readonly<Record<...>>` annotation alone does not
 * catch an excess/stale key once the literal is routed through `Object.freeze`.
 */
export const PROJECT_UNDELIVERED_REASON_KEYS = Object.freeze({
    'empty-snapshot': true,
    'build-failed': true,
    'delivery-failed': true,
    'not-attempted': true,
});
export const PROJECT_UNDELIVERED_REASONS = Object.freeze(Object.keys(PROJECT_UNDELIVERED_REASON_KEYS));
//# sourceMappingURL=project.schema.js.map